import { useCallback, useEffect, useRef } from 'react';
import { useMarkActivitySeenMutation } from '@/redux/activity/activity.api.slice';

const FLUSH_MS = 1000;
const MAX_BATCH = 100;

/** Collects ids of rows the user actually scrolled past and reports them in batches. */
export default function useSeenReporter() {
  const [markSeen] = useMarkActivitySeenMutation();
  const pending = useRef(new Set<string>());
  const reported = useRef(new Set<string>());
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    const ids = [...pending.current].slice(0, MAX_BATCH);
    ids.forEach((id) => pending.current.delete(id));
    if (ids.length > 0) markSeen(ids);
    if (pending.current.size > 0) timer.current = setTimeout(flush, FLUSH_MS);
  }, [markSeen]);

  useEffect(() => flush, [flush]);

  return useCallback(
    (id: string) => {
      if (reported.current.has(id)) return;
      reported.current.add(id);
      pending.current.add(id);
      clearTimeout(timer.current);
      timer.current = setTimeout(flush, FLUSH_MS);
    },
    [flush]
  );
}
